<template>
  <div>
    <v-card rounded outlined>
      <div class="d-flex" style="overflow:hidden;">
        <div :style="{width:'9px', backgroundColor: scoreColor}">
          &nbsp;
        </div>
        <div style="width:100%" @click="showDetails = !showDetails" :class="{'param-title':true, 'param-title-selected':showDetails}">
          <v-card-title>
            {{ $t('result.motivation.title') }}
            <v-spacer></v-spacer>
            <div class="d-flex align-center">
              <!-- approach / energy balls -->
              <score-bar
                  v-if="showDetails"
                  :options="{type:'title_bar',values:[motivation.approach.value,motivation.energy.value],scale:2,average: true}"
              ></score-bar>
              <a class="subtitle-2 ml-2" style="text-decoration: none" href="javascript:void(0)">
                <span v-if="showDetails">{{$t('result.toggle_summary')}}</span>
                <span v-else>{{$t('result.toggle_detail')}}</span>
              </a>
            </div>
          </v-card-title>

          <!-- summary content -->
          <div v-if="!showDetails" class="px-5 mb-5">
            <!-- SMALL -->
            <v-card flat rounded class="d-sm-none">
              <v-card-text class="py-5">
                <score-bar
                    :title="$t('result.motivation.approach.title')"
                    :value="motivation.approach.value"
                    :options="{type:'summary',scale:2, average: true}"
                >
                </score-bar>
                <score-bar
                    :title="$t('result.motivation.energy.title')"
                    :value="motivation.energy.value"
                    :options="{type:'summary',scale:2, average: true}"
                >
                </score-bar>
              </v-card-text>
            </v-card>

            <!-- NORMAL -->
            <v-card flat rounded class="d-none d-sm-block">
              <v-card-text class="py-5 d-flex justify-center">
                  <v-row>
                    <!-- Approach & Energy -->
                    <v-col cols="12" sm="6" v-for="(item, key) in [motivation.approach.value,motivation.energy.value]" :key="key">
                        <div class="d-flex justify-center">
                          <div style="width:60px;">
                            <score-bar
                                :value="item"
                                :options="{type:'stack',scale:2, average: true}"
                            ></score-bar>
                          </div>
                        </div>
                        <div class="text-center mt-2">
                          <p class="ma-0 body-2" style="margin-top:-3px !important;">
                            {{$t('result.motivation.'+ae_keys[key]+'.title')}}
                            <br>
                            <span class="caption"><span class="grey--text">Ø </span>{{scoreNum(item)}}</span>
                          </p>
                        </div>
                    </v-col>
                  </v-row>
              </v-card-text>
            </v-card>
          </div>
        </div>

      </div>
      <div v-if="showDetails"
           style="width:100%;position:absolute;top:50px;"
           class="text-center d-print-none"
      >
          <v-btn @click="showDetailsLegend = !showDetailsLegend"
                 small elevation="0"
                 class="rounded-0"
          >
              <v-icon v-if="showDetailsLegend" color="green" small>mdi-text-box-check</v-icon>
              <v-icon v-else small>mdi-text-box-outline</v-icon>
          </v-btn>
      </div>
      <div v-if="showDetails && !showDetailsLegend">
          <v-divider></v-divider>
          <v-card-text class="px-5">
              <div v-for="(item,key) in [motivation.approach,motivation.energy]"
                   :key="key"
              >
                  <distribution
                          class="my-2 px-2"
                          :title="$t('result.motivation.'+ae_keys[key]+'.title')"
                          :value="item.value"
                          :distro="item.distro"
                          :options="{average: true, boldScore: true}"
                          type="motivation"
                  />
              </div>
          </v-card-text>
      </div>
      <div v-else-if="showDetails">
        <v-divider></v-divider>
        <div>
          <v-card-text class="py-5">
            <!-- Approach & Energy -->
            <div v-for="(item,key) in [motivation.approach,motivation.energy]"
                   :key="key"
                   align="center"
                   :class="{'bm-page-break': key === 1, 'mb-5': true}"
            >
              <div class="pb-0">
                <div class="text-center mt-2">
                      <span class="subtitle-1 font-weight-bold text-uppercase" :style="{borderBottom: score_meta[key].color+' 2px solid'} ">
                        {{$t('result.motivation.'+ae_keys[key]+'.title')}}
                      </span><br>
                  <span class="body-2">{{$t('result.motivation.'+ae_keys[key]+'.text')}}</span>
                </div>
                <distribution
                        class="my-2 px-2"
                        :value="item.value"
                        :distro="item.distro"
                        :options="{average: true, boldScore: true}"
                        type="motivation"
                />
              </div>
              <div class="pt-0">
                <v-card flat rounded color="back_accent" class="mt-3">
                  <v-card-text class="text-center text-sm-left py-print" style="padding-top:20px;">
                    <div v-for="(state,skey) in ae_states">
                      <score-bar
                          v-if="score_meta[key].state === skey"
                          :value="item.value"
                          :options="{type:'inline',scale:2, average: true}"
                      ></score-bar>
                      <strong>{{ $t('result.motivation.'+ae_keys[key]+'.state.'+skey+'.title') }}</strong>
                      ({{state}})
                      <p class="body-2">
                        {{ $t('result.motivation.'+ae_keys[key]+'.state.'+skey+'.text') }}
                      </p>
                    </div>
                  </v-card-text>
                </v-card>
              </div>
            </div>
          </v-card-text>
        </div>
      </div>

    </v-card>
  </div>
</template>

<script>
  import Distribution from "@/components/team/distribution.vue";

  export default {
    name: "teamMotivation",
    props: ['motivation'],
    components: {
        Distribution,
      ScoreBar: () => import('../ScoreBar'),
    },
    data() {
      return {
        showDetails: false,
        showDetailsLegend: false,
        ae_keys: {
          0: "approach",
          1: "energy"
        },
        score_meta: {
          0: {color:'#ddd',state:'mid'},
          1: {color:'#ddd',state:'mid'}
        },
        ae_states: {
            "high":"9.0-10",
            "mhigh":"7.0-8.9",
            "mid":"5.0-6.9",
            "mlow":"3.0-4.9",
            "low":"1.0-2.9"
        },
        scoreColor: 'transparent'
      }
    },
    computed: {
      colors() {
        return this.$store.getters['app/colors']
      },
      legends() {
        return this.$store.getters['app/legends']
      },
      showAll(){
        return this.$store.getters['diagnostic/showAll']
      },
      showLegends() {
          return this.$store.getters['diagnostic/showLegends']
      },
    },
    methods: {
       getScoreMeta() {
         let scores = [this.motivation.approach.value, this.motivation.energy.value]

         //summary color left stripe
         this.$store.dispatch('app/getSummaryColor',{scores:scores,type:2}).then(score_color => {
           this.scoreColor = score_color
         })

         //result colors
         for (let item in scores) {
           this.$store.dispatch('app/getSummaryColor',{scores:[scores[item]],type:2}).then(score_color => {
             this.score_meta[item].color = score_color
           })
           if (scores[item] < 3) {
             this.score_meta[item].state = 'low'
           } else if (scores[item] < 5) {
             this.score_meta[item].state = 'mlow'
           } else if (scores[item] < 7) {
             this.score_meta[item].state = 'mid'
           } else if (scores[item] < 9) {
             this.score_meta[item].state = 'mhigh'
           } else {
             this.score_meta[item].state = 'high'
           }
         }
      },
      scoreNum(score){
        if (score < 9.947) {
          return score.toFixed(1)
        } else {
          return 10
        }
      }
    },
    created() {
      this.showDetails = this.showAll
      this.getScoreMeta()
    },
    watch: {
      showAll: function(val) {
        this.showDetails = val
      },
      showLegends: function(val) {
          this.showDetailsLegend = val
      }
    }
  }
</script>
